"use client";

import { LinkButton } from "@repo/ui/link-button";
import { cn } from "@repo/ui/utils";
import { useTranslations } from "next-intl";
import { useAudience } from "@/components/common/audience";
import { Link } from "@/i18n/navigation";
import { useBusinessHomeHref } from "@/components/navigation/use-home-href";

interface AudienceTabsProps {
  variant?: "desktop" | "mobile";
}

export function AudienceTabs({ variant = "desktop" }: AudienceTabsProps) {
  const { activeTab, setActiveTab } = useAudience();
  const businessHomeHref = useBusinessHomeHref();
  const t = useTranslations("navigation");

  const isMobile = variant === "mobile";

  const wrapperClass = isMobile
    ? "grid grid-cols-2"
    : "flex items-stretch gap-9";

  // Tab indicator uses border-b (not LinkButton's underline stripe) so the
  // line sits at the bottom of the padded box and aligns with the
  // utility-bar's `border-b border-border-subtle` divider as one continuous
  // line under the active tab.
  //
  // - `inline-flex items-center` overrides base `display: inline` so the tab
  //   can stretch to the parent's `items-stretch` height.
  // - `bg-none` suppresses the base LinkButton background-image underline.
  // - `pb-0` overrides the base `pb-2` so the border-b alignment to the
  //   divider isn't pushed down by the stripe's padding reservation.
  const tabClass = (active: boolean) =>
    cn(
      "inline-flex items-center justify-center py-4 pb-4 bg-none border-b",
      active
        ? "border-border-interactive"
        : "border-transparent hover:border-border-interactive focus-visible:border-border-interactive",
    );

  return (
    <div className={wrapperClass} role="group" aria-label={t("audience")}>
      <LinkButton
        asChild
        variant={activeTab === "visitors" ? "primary" : "secondary"}
        size={"sm"}
        className={tabClass(activeTab === "visitors")}
        aria-current={activeTab === "visitors" ? "true" : undefined}
      >
        <Link href="/" onClick={() => setActiveTab("visitors")}>
          {t("forVisitors")}
        </Link>
      </LinkButton>
      <LinkButton
        asChild
        variant={activeTab === "businesses" ? "primary" : "secondary"}
        size={"sm"}
        className={tabClass(activeTab === "businesses")}
        aria-current={activeTab === "businesses" ? "true" : undefined}
      >
        {businessHomeHref ? (
          <Link
            href={businessHomeHref}
            onClick={() => setActiveTab("businesses")}
          >
            {t("forBusinesses")}
          </Link>
        ) : (
          <button type="button" onClick={() => setActiveTab("businesses")}>
            {t("forBusinesses")}
          </button>
        )}
      </LinkButton>
    </div>
  );
}
